'use client'

import { HugeiconsIcon } from '@hugeicons/react'
import {
  AiBrain04Icon,
  ArrowDown01Icon,
  ArrowUp01Icon,
  Copy01Icon,
  Delete02Icon,
  Flag02Icon,
  Flowchart02Icon,
  MoreVerticalIcon,
  PencilEdit02Icon,
  VariableIcon,
  WorkflowCircle06Icon,
} from '@hugeicons-pro/core-stroke-rounded'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'
import type { Step } from '@/lib/commands/graph-serialize'
import { graphToolMetaByName } from '@/lib/commands/graph-tools-meta'
import { AiStepEditor } from './steps/AiStepEditor'
import { ConditionStepEditor } from './steps/ConditionStepEditor'
import { ToolStepEditor } from './steps/ToolStepEditor'
import { TransformStepEditor } from './steps/TransformStepEditor'

export function StepCard({
  step,
  index,
  steps,
  expanded,
  readOnly,
  errors,
  includeEvent,
  onToggleExpanded,
  onChange,
  onMove,
  onDuplicate,
  onDelete,
}: {
  step: Step
  index: number
  steps: Step[]
  expanded: boolean
  readOnly: boolean
  errors: string[]
  includeEvent: boolean
  onToggleExpanded: () => void
  onChange: (step: Step) => void
  onMove: (direction: 'up' | 'down') => void
  onDuplicate: () => void
  onDelete: () => void
}) {
  const priorStepIds = steps.slice(0, index).map((candidate) => candidate.id)
  const priorVars = steps
    .slice(0, index)
    .flatMap((candidate) => candidate.kind === 'transform' ? Object.keys(candidate.assign) : [])
  const onlyEnd = step.kind === 'end' && steps.filter((candidate) => candidate.kind === 'end').length === 1
  const canMoveUp = index > 0 && step.kind !== 'end'
  const canMoveDown = index < steps.length - 1 && steps[index + 1]?.kind !== 'end'

  return (
    <div className={cn(
      'rounded-lg border bg-white',
      errors.length > 0 ? 'border-red-300' : 'border-atrium-border',
      readOnly && 'opacity-70',
    )}>
      <div className="flex items-start gap-3 p-4">
        <div className="grid size-7 shrink-0 place-items-center rounded-full bg-atrium-content text-xs font-semibold text-atrium-black">
          {index + 1}
        </div>
        <HugeiconsIcon icon={iconForStep(step)} className="mt-1 size-5 shrink-0 text-atrium-black/70" />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <h4 className="text-sm font-semibold text-atrium-black">{labelForStep(step)}</h4>
            <span className="font-mono text-xs text-atrium-black/50">{step.id}</span>
          </div>
          <p className="mt-1 truncate text-sm text-atrium-black/60">{summaryForStep(step)}</p>
          {errors.length > 0 && (
            <div className="mt-2 space-y-1">
              {errors.map((error) => (
                <p key={error} className="text-xs text-red-600">{error}</p>
              ))}
            </div>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {step.kind !== 'end' && (
            <Button type="button" variant="ghost" size="icon" onClick={onToggleExpanded} disabled={readOnly} aria-label="Edit step">
              <HugeiconsIcon icon={PencilEdit02Icon} className="size-4" />
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button type="button" variant="ghost" size="icon" disabled={readOnly} aria-label="Step actions">
                <HugeiconsIcon icon={MoreVerticalIcon} className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem disabled={!canMoveUp} onClick={() => onMove('up')}>
                <HugeiconsIcon icon={ArrowUp01Icon} className="size-4" />
                Move up
              </DropdownMenuItem>
              <DropdownMenuItem disabled={!canMoveDown} onClick={() => onMove('down')}>
                <HugeiconsIcon icon={ArrowDown01Icon} className="size-4" />
                Move down
              </DropdownMenuItem>
              <DropdownMenuItem disabled={step.kind === 'end'} onClick={onDuplicate}>
                <HugeiconsIcon icon={Copy01Icon} className="size-4" />
                Duplicate
              </DropdownMenuItem>
              <DropdownMenuItem disabled={onlyEnd} onClick={onDelete} variant="destructive">
                <HugeiconsIcon icon={Delete02Icon} className="size-4" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {expanded && !readOnly && step.kind !== 'end' && (
        <div className="border-t border-atrium-border p-4">
          {step.kind === 'tool' && (
            <ToolStepEditor step={step} priorStepIds={priorStepIds} includeEvent={includeEvent} onChange={onChange} />
          )}
          {step.kind === 'ai_step' && (
            <AiStepEditor step={step} priorStepIds={priorStepIds} includeEvent={includeEvent} onChange={onChange} />
          )}
          {step.kind === 'condition' && (
            <ConditionStepEditor step={step} steps={steps} priorStepIds={priorStepIds} onChange={onChange} />
          )}
          {step.kind === 'transform' && (
            <TransformStepEditor step={step} priorStepIds={priorStepIds} priorVars={priorVars} onChange={onChange} />
          )}
        </div>
      )}
    </div>
  )
}

function iconForStep(step: Step) {
  if (step.kind === 'tool') return WorkflowCircle06Icon
  if (step.kind === 'ai_step') return AiBrain04Icon
  if (step.kind === 'condition') return Flowchart02Icon
  if (step.kind === 'transform') return VariableIcon
  return Flag02Icon
}

function labelForStep(step: Step) {
  if (step.kind === 'tool') return 'Fetch data'
  if (step.kind === 'ai_step') return 'Ask AI to summarize'
  if (step.kind === 'condition') return 'If / then'
  if (step.kind === 'transform') return 'Set a variable'
  return 'End'
}

function summaryForStep(step: Step) {
  if (step.kind === 'tool') return graphToolMetaByName.get(step.tool)?.label ?? (step.tool || 'Pick a tool')
  if (step.kind === 'ai_step') return step.prompt.trim() ? quote(step.prompt.trim()) : 'Prompt is required'
  if (step.kind === 'condition') return step.expr.trim() ? `If ${step.expr}, then ${step.then}, else ${step.else}` : 'Condition is required'
  if (step.kind === 'transform') {
    const keys = Object.keys(step.assign)
    return keys.length > 0 ? `Assign ${keys.join(', ')}` : 'Add at least one variable'
  }
  return 'Stop the command'
}

function quote(value: string) {
  const firstLine = value.split('\n')[0] ?? value
  return `"${firstLine}"`
}
